import RestService from '@core/services/offer/_private/RestService';
import Match from '@core/models/offer/Match';
import { Subscription } from 'rxjs';
import SocketManager from '@core/services/offer/_private/SocketManager';
import StructManager from '@core/services/common/StructManager';
import SuperOffer from '@core/models/offer/SuperOffer';
import OfferRequest from '@core/services/offer/_private/models/OfferRequest';
import ServerConfig from '@core/models/country/ServerConfig';
// import VisualisationService from './_private/VisualisationService';
import { instanceGuard } from '@core/utils/services';
import SingletonError from '../common/errors/SingletonError';
import Singleton from '../common/Singleton';
import EventMapper from '@core/mappers/offer/EventMapper';
import SuperOfferMapper from '@core/mappers/offer/SuperOfferMapper';
import * as NetworkUtils from '@utils/network/NetworkUtils';
import IResponse from '@utils/types/IResponse';
import IEventDto from '@core/mappers/offer/dtos/IEventDto';
import MaintenanceService from '@core/services/maintenance/MaintenanceService';
import { ISuperOfferDto } from '@core/mappers/offer/dtos/ISuperOfferDto';
import Sport from '@models/struct/Sport';
import Category from '@models/struct/Category';
import Tournament from '@models/struct/Tournament';
import Market from '@models/struct/Market';
import OddType from '@models/struct/OddType';
import SSEManager, { SSEEventMessage } from '@core/services/offer/_private/SSEManager';
import MarketGroupInfo from '@models/struct/MarketGroupInfo';

/**
 * Important!
 *
 * 1. Client is obliged to call startStreaming(struct) after struct is fetched and
 * before using any other method from OfferService.
 *
 * For example:
 *
 *  - struct = await offerService.getStruct()
 *  - offerService.startStreaming(struct) // after that you can use service normally
 *  - offerService.getEvents()
 *  - offerService.subscribeToEvent()
 * ...
 *
 * 2. Client is obliged to call startStreaming(struct) (and fetch new struct) after
 * every call to stopStreaming().
 *
 * For example:
 *  - switch to casino page
 *  - call offerService.stopStreaming()
 *  - switch back to sports page
 *  - struct = await offerService.getStruct()
 *  - offerService.startStreaming(struct) // after that you can use service normally
 *  - offerService.getEvents()
 *  - offerService.subscribeToEvent()
 *
 */

export interface Dependencies {
    offerHostProvider: { getCurrentOfferHost: () => string; getCurrentOfferHostSocket: () => string; };
}

// tslint:disable-next-line variable-name
class OfferService {
    private static instance: OfferService;
    private socketManager: SocketManager;
    private sseManager: SSEManager;
    private restService: RestService;
    private eventMapper: EventMapper = new EventMapper();
    private superOfferMapper: SuperOfferMapper = new SuperOfferMapper();
    // private visualisationService: VisualisationService;
    private useSSE: boolean;

    private constructor(private config: ServerConfig, { offerHostProvider }: Dependencies) {
        if (OfferService.instance) {
            throw new SingletonError(this.constructor.name);
        }
        console.log('SSBT::OfferService hostServer sockets >>', offerHostProvider.getCurrentOfferHostSocket());
        console.log('SSBT::OfferService hostServer >>', offerHostProvider.getCurrentOfferHost());
        this.restService = new RestService(offerHostProvider.getCurrentOfferHost());
        // this.visualisationService = new VisualisationService(this.config);
        this.useSSE = true;
        this.socketManager = new SocketManager(offerHostProvider.getCurrentOfferHostSocket());
        this.sseManager = new SSEManager(offerHostProvider.getCurrentOfferHost());
    }

    public static getInstance(): OfferService {
        return instanceGuard(OfferService.instance);
    }

    public static createInstance(config: ServerConfig, dependencies: Dependencies) {
        if (!OfferService.instance) {
            OfferService.instance = new OfferService(config, dependencies);
        }
        return OfferService.instance;
    }

    /**
    * @throws {RequestError}
    * @throws {Error}
    */
    public getLiveEvents(startDate: Date): Promise<Match[]> {
        return this.fetchEvents(() => this.restService.getLiveEvents(startDate));
    }

    public sseEnabled() {
        return this.useSSE;
    }

    /**
     * @throws {RequestError}
     * @throws {Error}
     */
    public getEvents(startDate: Date, endDate: Date): Promise<Match[]> {
        return this.fetchEvents(() => this.restService.getEvents(startDate, endDate));
    }

    /**
     * @throws {RequestError}
     * @throws {Error}
     */
    public getOutrights(): Promise<Match[]> {
        return this.fetchEvents(() => this.restService.getOutrights());
    }

    /**
     * @throws {RequestError}
     * @throws {Error}
     */
    public async getEventsById(eventIds: number[]): Promise<Match[]> {
        const matches = await this.restService.getEventsById(eventIds);
        StructManager.getInstance().denormalizeEvents(matches);
        return matches;
    }

    /**
 * @throws {RequestError}
 * @throws {Error}
 */
    public async getEvent(id: number) {
        const [match] = await this.getEventsById([id]);
        return match || null;
    }

    /**
     * @throws {RequestError}
     * @throws {Error}
     */
    public getEventsBySport(sportId: number, startDate: Date, endDate: Date): Promise<Match[]> {
        // todo remove socket, currently not in use
        return this.socketManager.getEventsBySport(sportId, startDate, endDate);
    }

    /**
     * @throws {RequestError}
     * @throws {Error}
     */
    public getEventsByTournament(
        tournamentId: number, startDate: Date,
        endDate: Date, extraParams?: OfferRequest): Promise<Match[]> {
        // todo remove socket, currently not in use
        return this.socketManager.getEventsByTournament(tournamentId, startDate, endDate, extraParams);
    }

    /**
     * @throws {RequestError}
     * @throws {Error}
     */
    public async getSuperEvents(startDate: Date, endDate: Date): Promise<SuperOffer> {
        const response: any = await NetworkUtils
            .processResponse(this.restService.getSuperEvents(startDate, endDate));
        const superOfferDto: ISuperOfferDto = response.data;
        const superOffer = this.superOfferMapper.map(superOfferDto);

        StructManager.getInstance().denormalizeEvents(superOffer.superExtra);
        StructManager.getInstance().denormalizeEvents(superOffer.superKvota);
        superOffer.superExtra = this.removeEventsInMaintenance(superOffer.superExtra);
        superOffer.superKvota = this.removeEventsInMaintenance(superOffer.superKvota);
        return superOffer;
    }

    /**
     * @throws {RequestError}
     * @throws {Error}
     */
    public getTopTenEvents(): Promise<Match[]> {
        return this.fetchEvents(() => this.restService.getTopTenEvents());
    }

    /**
     * todo we dont have any notification for subscription.  if socket isn't connected
     * todo subcription will go to queue. there is no timeout. we will see later is this is good aproach
     * @return Subscription object so you can obj.unsubscribe()
     */
    public subscribeToEvent(
        eventId: number,
        callback: (event: Match) => void
    ): Subscription {
        return this.socketManager.subscribeToEventStream(eventId, callback);
    }
    public subscribeToEventSSE(
        eventId: number,
        callback: ({ resourceId, timestamp, event }: SSEEventMessage) => void
    ): Subscription {
        return this.sseManager.subscribeToEventChanges(eventId, callback);
    }

    /**
     *
     * todo we dont have any notification for subscription.  if socket isn't connected
     * todo subcription will go to queue. there is no timeout. we will see later is this is good aproach
     * @return Subscription object so you can obj.unsubscribe()
     */
    public subscribeToEvents(
        eventIds: number[],
        callback: (events: Match[]) => void
    ): Subscription {
        return this.socketManager.subscribeToEventsStream(eventIds, callback);
    }
    public subscribeToSportEvent(callback: (sport: Sport) => void): void {
        this.socketManager.subscribeToSportEvent(callback);
    }

    public subscribeToCategoryEvent(callback: (category: Category) => void): void {
        this.socketManager.subscribeToCategoryEvent(callback);
    }

    public subscribeToTournamentEvent(callback: (tournament: Tournament) => void): void {
        this.socketManager.subscribeToTournamentEvent(callback);
    }
    public subscribeToMarketEvent(callback: (market: Market) => void): void {
        this.socketManager.subscribeToMarketEvent(callback);
    }

    public subscribeToOddTypeEvent(callback: (oddType: OddType) => void): void {
        this.socketManager.subscribeToOddTypeEvent(callback);
    }

    public subscribeToMarketGroupInfoEvent(callback: (marketGroup: MarketGroupInfo) => void): void {
        this.socketManager.subscribeToMarketGroupEvent(callback);
    }
    /**
     * Subscribe to stream with callback, if system changes struct
     * the stream will emit (value isnt important) and call callback.  In your callback
     * fetch struct again from REST API
     *
     * @return Subscription object so you can obj.unsubscribe()
     */
    public subscribeToStructChange(callback: () => void): Subscription {
        return this.socketManager.subscribeToStructChangeStream(callback);
    }

    /**
     * Subscribe to stream with callback.  For every match change this method
     * will call calback with *list* of changed/new Events.
     *
     * @return Subscription object so you can obj.unsubscribe()
     */
    public subscribeToAllEventChanges(callback: (events: Match[]) => void): Subscription {
        return this.socketManager.subscribeToAllEventChangesStream(callback);
    }

    public subscribeToAllEventChangesSSE(
        callback: ({ resourceId, timestamp, event }: SSEEventMessage) => void
    ): Subscription {
        return this.sseManager.subscribeToAllEventChanges(callback);
    }

    public subscribeToLiveEventChangesSSE(
        callback: (events: any[]) => void
    ): Subscription {
        return this.sseManager.subscribeToLiveEventChanges(callback);
    }

    public subscribeToAllOutrightEventChanges(
        callback: ({ resourceId, timestamp, event }: SSEEventMessage) => void
    ): Subscription {
        return this.sseManager.subscribeToAllOutrightEventChanges(callback);
    }

    /**
     * Subscribe to stream with callback. This method will call callback for every live
     * event change.
     *
     * @return Subscription object so you can obj.unsubscribe()
     */
    public subscribeToLiveEventsCountChangesStream(callback: (eventsCount: number) => void): Subscription {
        return this.socketManager.subscribeToLiveEventsCountChangesStream(callback);
    }

    /**
     * When stream can't connect the caller will be notified
     *
     * @return Subscription object so you can obj.unsubscribe()
     */
    public subscribeToConnectErrorStream(callback: (error: Error) => void): Subscription {
        return this.socketManager.subscribeToConnectErrorStream(callback);
    }

    /**
     *  When stream disconnects the caller will be notified
     *
     * @return Subscription object so you can obj.unsubscribe()
     */
    public subscribeToDisconnectedStream(callback: () => void): Subscription {
        return this.socketManager.subscribeToDisconnectedStream(callback);
    }

    /**
     *  When stream connects the caller will be notified
     *
     * @return Subscription object so you can obj.unsubscribe()
     */
    public subscribeToConnectedStream(callback: () => void): Subscription {
        return this.socketManager.subscribeToConnectedStream(callback);
    }

    /**
     *  When stream connects the caller will be notified
     *
     * @return Subscription object so you can obj.unsubscribe()
     */
    public subscribeToErrorStream(callback: () => void): Subscription {
        return this.socketManager.subscribeToErrorStream(callback);
    }

    /**
     *
     * @throws {RequestError}
     * @throws {Error}
     */
    public stopStreaming(): void {
        if (!this.useSSE) {
            this.socketManager.stopStreaming();
        } else {
            this.sseManager.unsubscribeFromStreams();
        }
    }

    /**
     * @throws {RequestError}
     * @throws {Error}
     */
    public startStreaming(): void {
        if (!this.useSSE) {
            this.socketManager.startStreaming();
        }
    }

    public setRestService(restService: RestService) {
        this.restService = restService;
    }

    public setSocketManager(socketManager: SocketManager) {
        this.socketManager = socketManager;
    }

    public getSocketManager(): SocketManager {
        return this.socketManager;
    }

    public denormalizeEvent(event: Match) {
        StructManager.getInstance().denormalizeEvent(event);
    }

    // public checkMatchHasVisualisation(betRadarId: Match['betRadarId'], sportId: number) {
    //     return this.visualisationService.checkMatchHasVisualisation(betRadarId, sportId);
    // }

    // public getVisualisationUrl(betRadarId: string) {
    //     return this.visualisationService.createVisualisationiFrameUrl(betRadarId);
    // }

    private async fetchEvents(fetcher: Function) {
        const response: IResponse<IEventDto[]> = await NetworkUtils
            .processResponse(fetcher());
        const matches = this.eventMapper.map(response.data, true);
        StructManager.getInstance().denormalizeEvents(matches);
        return matches.filter(e => !MaintenanceService.getInstance().isEventInMaintenance(e));
    }

    private removeEventsInMaintenance = (events: Match[]): Match[] =>
        events.filter(e => !MaintenanceService.getInstance().isEventInMaintenance(e));

    public static clearInstance() {
        if (process.env.NODE_ENV !== 'test') {
            throw new Error('For use in tests only');
        }
        delete OfferService.instance;
    }
}

export default OfferService as Singleton<OfferService>;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--12!./src/core/services/offer/OfferService.ts